// from https://github.com/apollographql/apollo-client/blob/a7566a635c5f48bdfcbb215270dff335cfa6a803/src/link/retry/retryLink.ts
import type { Operation, FetchResult, NextLink } from '@apollo/client';
import { ApolloLink } from '@apollo/client';
import type { ObservableSubscription } from '@apollo/client';
import { Observable } from '@apollo/client';
import type { DelayFunction, DelayFunctionOptions } from '@apollo/client/link/retry/delayFunction';
import { buildDelayFunction } from '@apollo/client/link/retry/delayFunction';
import type { RetryFunction, RetryFunctionOptions } from '@apollo/client/link/retry/retryFunction';
import { buildRetryFunction } from '@apollo/client/link/retry/retryFunction';
import { ApolloError } from '@apollo/client';
import { graphQLResultHasProtocolErrors, PROTOCOL_ERRORS_SYMBOL } from '@apollo/client/errors';
import type { SubscriptionObserver } from 'zen-observable-ts';
import { Kind, type OperationDefinitionNode } from 'graphql';

export interface RetryLinkOptions {
  /**
   * Configuration for the delay strategy to use, or a custom delay strategy.
   */
  delay?: DelayFunctionOptions | DelayFunction;

  /**
   * Configuration for the retry strategy to use, or a custom retry strategy.
   */
  attempts?: RetryFunctionOptions | RetryFunction;
}

/**
 * Tracking and management of operations that may be (or currently are) retried.
 * In the future apollo is considering supporting retries for not-only network errors.
 * Today, only network errors are retried. As a result. I moved the code from apollo-client to here and modified to support also retrying queries.
 */
class RetryableOperation {
  private retryCount = 0;
  private currentSubscription: ObservableSubscription | null = null;
  private timerId: number | undefined;

  constructor(
    private observer: SubscriptionObserver<FetchResult>,
    private operation: Operation,
    private forward: NextLink,
    private delayFor: DelayFunction,
    private retryIf: RetryFunction
  ) {
    this.try();
  }

  /**
   * Stop retrying for the operation, and cancel any in-progress requests.
   */
  public cancel() {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
    }
    clearTimeout(this.timerId);
    this.timerId = undefined;
    this.currentSubscription = null;
  }

  private try() {
    this.currentSubscription = this.forward(this.operation).subscribe({
      next: (result) => {
        if (graphQLResultHasProtocolErrors(result)) {
          this.onError(
            new ApolloError({
              protocolErrors: result.extensions[PROTOCOL_ERRORS_SYMBOL],
            })
          );
          // Unsubscribe from the current subscription to prevent the `complete`
          // handler to be called as a result of the stream closing.
          this.currentSubscription?.unsubscribe();
          return;
        }

        if (!result.errors || result.errors.length === 0) {
          // make sure we don't retry successfull operations
          this.observer.next(result);
          return;
        }

        const operationDefinitions: OperationDefinitionNode[] = this.operation.query.definitions.filter(
          (def): def is OperationDefinitionNode => def.kind === Kind.OPERATION_DEFINITION
        );

        // don't retry mutations
        if (operationDefinitions.some((def: OperationDefinitionNode) => def.operation === 'mutation')) {
          this.observer.next(result);
          return;
        }

        console.log('Retrying operation due to error', this.operation.operationName);

        this.onError(
          new ApolloError({
            graphQLErrors: result.errors,
          })
        );
        // Unsubscribe from the current subscription to prevent the `complete`
        // handler to be called as a result of the stream closing.
        this.currentSubscription?.unsubscribe();
        return;
      },
      error: this.onError,
      complete: this.observer.complete.bind(this.observer),
    });
  }

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  private onError = async (error: any) => {
    this.retryCount += 1;

    // Should we retry?
    const shouldRetry = await this.retryIf(this.retryCount, this.operation, error);
    if (shouldRetry) {
      this.scheduleRetry(this.delayFor(this.retryCount, this.operation, error));
      return;
    }

    this.observer.error(error);
  };

  private scheduleRetry(delay: number) {
    if (this.timerId) {
      throw new Error('RetryLink BUG! Encountered overlapping retries');
    }

    this.timerId = setTimeout(() => {
      this.timerId = undefined;
      this.try();
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    }, delay) as any as number;
  }
}

export class RetryLink extends ApolloLink {
  private delayFor: DelayFunction;
  private retryIf: RetryFunction;

  constructor(options?: RetryLinkOptions) {
    super();
    const { attempts, delay } = options || ({} as RetryLinkOptions);
    this.delayFor = typeof delay === 'function' ? delay : buildDelayFunction(delay);
    this.retryIf = typeof attempts === 'function' ? attempts : buildRetryFunction(attempts);
  }

  public request(operation: Operation, nextLink: NextLink): Observable<FetchResult> {
    return new Observable((observer) => {
      const retryable = new RetryableOperation(observer, operation, nextLink, this.delayFor, this.retryIf);
      return () => {
        retryable.cancel();
      };
    });
  }
}
